/**
 * Utilitaires pour calculer les jours fériés français
 *
 * Ce fichier fournit des fonctions pour :
 * - Calculer les jours fériés fixes (1er janvier, 1er mai, 14 juillet, etc.)
 * - Calculer les jours fériés mobiles (Pâques, Ascension, Pentecôte)
 * - Vérifier si une date est un jour férié
 */

/**
 * Calcule la date de Pâques pour une année donnée
 * Utilise l'algorithme de Meeus/Jones/Butcher
 *
 * @param {number} year - Année (ex: 2025)
 * @returns {Date} Date de Pâques
 */
function calculateEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1; // 0-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month, day);
}

/**
 * Ajoute un nombre de jours à une date
 *
 * @param {Date} date - Date de base
 * @param {number} days - Nombre de jours à ajouter
 * @returns {Date} Nouvelle date
 */
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Calcule tous les jours fériés français pour une année donnée
 *
 * Jours fériés inclus :
 * - 1er janvier (Jour de l'an)
 * - Lundi de Pâques (mobile)
 * - 1er mai (Fête du travail)
 * - 8 mai (Victoire 1945)
 * - Jeudi de l'Ascension (mobile, 39 jours après Pâques)
 * - Lundi de Pentecôte (mobile, 50 jours après Pâques)
 * - 14 juillet (Fête nationale)
 * - 15 août (Assomption)
 * - 1er novembre (Toussaint)
 * - 11 novembre (Armistice 1918)
 * - 25 décembre (Noël)
 *
 * @param {number} year - Année (ex: 2025)
 * @returns {Array<Date>} Liste des jours fériés
 */
export function getFrenchHolidays(year) {
  const holidays = [];

  // Jours fériés fixes
  holidays.push(new Date(year, 0, 1));    // 1er janvier - Jour de l'an
  holidays.push(new Date(year, 4, 1));    // 1er mai - Fête du travail
  holidays.push(new Date(year, 4, 8));    // 8 mai - Victoire 1945
  holidays.push(new Date(year, 6, 14));   // 14 juillet - Fête nationale
  holidays.push(new Date(year, 7, 15));   // 15 août - Assomption
  holidays.push(new Date(year, 10, 1));   // 1er novembre - Toussaint
  holidays.push(new Date(year, 10, 11));  // 11 novembre - Armistice 1918
  holidays.push(new Date(year, 11, 25));  // 25 décembre - Noël

  // Jours fériés mobiles (basés sur Pâques)
  const easter = calculateEaster(year);
  holidays.push(addDays(easter, 1));      // Lundi de Pâques
  holidays.push(addDays(easter, 39));     // Jeudi de l'Ascension
  holidays.push(addDays(easter, 50));     // Lundi de Pentecôte

  // Normaliser toutes les dates à minuit pour les comparaisons
  return holidays.map(date => {
    date.setHours(0, 0, 0, 0);
    return date;
  });
}

/**
 * Vérifie si une date est un jour férié français
 *
 * @param {Date} date - Date à vérifier
 * @param {Array<Date>} holidays - Liste des jours fériés (optionnel, sera calculée si non fournie)
 * @returns {boolean} true si la date est un jour férié
 */
export function isFrenchHoliday(date, holidays = null) {
  const year = date.getFullYear();
  const holidaysList = holidays || getFrenchHolidays(year);

  // Normaliser la date à comparer
  const dateToCheck = new Date(date);
  dateToCheck.setHours(0, 0, 0, 0);

  return holidaysList.some(holiday =>
    holiday.getTime() === dateToCheck.getTime()
  );
}

/**
 * Vérifie si une date est le 1er mai spécifiquement
 * (car ce jour a une majoration spéciale de +100%)
 *
 * @param {Date} date - Date à vérifier
 * @returns {boolean} true si c'est le 1er mai
 */
export function isMay1st(date) {
  return date.getMonth() === 4 && date.getDate() === 1;
}

/**
 * Obtient tous les jours fériés pour une plage de dates
 * Utile pour les périodes qui couvrent plusieurs années
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {Array<Date>} Liste des jours fériés dans la plage
 */
export function getHolidaysInRange(startDate, endDate) {
  const holidays = [];
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // Récupérer les jours fériés pour chaque année de la plage
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = getFrenchHolidays(year);
    holidays.push(...yearHolidays);
  }

  // Filtrer pour garder seulement les jours dans la plage
  return holidays.filter(holiday =>
    holiday >= startDate && holiday <= endDate
  );
}

/**
 * Obtient le nom du jour férié
 *
 * @param {Date} date - Date du jour férié
 * @returns {string|null} Nom du jour férié ou null si pas un jour férié
 */
export function getHolidayName(date) {
  const month = date.getMonth();
  const day = date.getDate();
  const year = date.getFullYear();

  // Jours fixes
  if (month === 0 && day === 1) return "Jour de l'an";
  if (month === 4 && day === 1) return "Fête du travail";
  if (month === 4 && day === 8) return "Victoire 1945";
  if (month === 6 && day === 14) return "Fête nationale";
  if (month === 7 && day === 15) return "Assomption";
  if (month === 10 && day === 1) return "Toussaint";
  if (month === 10 && day === 11) return "Armistice 1918";
  if (month === 11 && day === 25) return "Noël";

  // Jours mobiles
  const easter = calculateEaster(year);
  const easterMonday = addDays(easter, 1);
  const ascension = addDays(easter, 39);
  const whitMonday = addDays(easter, 50);

  const dateToCheck = new Date(date);
  dateToCheck.setHours(0, 0, 0, 0);

  if (easterMonday.getTime() === dateToCheck.getTime()) return "Lundi de Pâques";
  if (ascension.getTime() === dateToCheck.getTime()) return "Ascension";
  if (whitMonday.getTime() === dateToCheck.getTime()) return "Lundi de Pentecôte";

  return null;
}

/**
 * Compte le nombre de jours fériés tombant sur un jour de la semaine spécifique
 * dans une plage de dates
 *
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {number} dayOfWeek - Jour de la semaine (0=dimanche, 1=lundi, ..., 6=samedi)
 * @returns {number} Nombre de jours fériés
 */
export function countHolidaysOnWeekday(startDate, endDate, dayOfWeek) {
  const holidays = getHolidaysInRange(startDate, endDate);
  return holidays.filter(holiday => holiday.getDay() === dayOfWeek).length;
}

export default {
  getFrenchHolidays,
  isFrenchHoliday,
  isMay1st,
  getHolidaysInRange,
  getHolidayName,
  countHolidaysOnWeekday
};
